import React, { useEffect, useState } from 'react';
import { CheckCircle, Briefcase, MapPin, Clock } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { VerifiedBadge } from '../designer-card/CardContent';
import { Badge } from '@/components/ui/badge';
import { getFlagEmoji } from '@/utils/countryFlags';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  image_url: string;
  images: Array<{
    id: string;
    url: string;
    order: number;
    caption?: string;
  }>;
  created_at: string;
  updated_at?: string;
  is_featured?: boolean;
  status?: "draft" | "published";
}

interface PortfolioFeedCardProps {
  profile: {
    id: string;
    username: string | null;
    username_slug?: string | null;
    display_name?: string | null;
    avatar_url: string | null;
    profession: string | null;
    location: string | null;
    portfolio_items?: PortfolioItem[];
    updated_at?: string;
  };
  onOpenPortfolioModal?: (profile: any, item: any) => void;
  isMobile?: boolean;
}

// Twitter-style relative timestamp component
const TwitterRelativeTime: React.FC<{ date: Date | string | null }> = ({ date }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Update every minute for most cases, every second for <1m
    let interval: NodeJS.Timeout;
    const update = () => setNow(new Date());
    if (!date) return;
    const d = typeof date === 'string' ? new Date(date) : date;
    const diff = (now.getTime() - d.getTime()) / 1000;
    if (diff < 60) {
      interval = setInterval(update, 1000);
    } else {
      interval = setInterval(update, 60000);
    }
    return () => clearInterval(interval);
  }, [date, now]);

  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffSec = Math.floor((now.getTime() - d.getTime()) / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWk = Math.floor(diffDay / 7);
  const diffMo = Math.floor(diffDay / 30);

  if (diffMin < 1) return <span>{Math.max(diffSec, 0)}s</span>;
  if (diffHr < 1) return <span>{diffMin}m</span>;
  if (diffDay < 1) return <span>{diffHr}h</span>;
  if (diffDay < 7) return <span>{diffDay}d</span>;
  if (diffDay < 28) return <span>{diffWk}w</span>;
  // Only show months for 28 days or more, and never show 0mo
  return <span>{Math.max(diffMo, 1)}mo</span>;
};

const PortfolioFeedCard: React.FC<PortfolioFeedCardProps> = ({ profile, onOpenPortfolioModal, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Debug logging
  console.log('PortfolioFeedCard: Received profile', { 
    profile, 
    hasProfile: !!profile, 
    profileId: profile?.id,
    hasPortfolioItems: !!profile?.portfolio_items 
  });

  // Early return if profile is undefined or null
  if (!profile) {
    console.warn('PortfolioFeedCard: Profile is undefined or null');
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-8 text-center">
          <p className="text-gray-500">No profile data available</p>
        </div>
      </div>
    );
  }

  // Replace getFeaturedPortfolioItem with getLastAddedPortfolioItem
  const getLastAddedPortfolioItem = (): PortfolioItem | null => {
    if (!profile.portfolio_items || profile.portfolio_items.length === 0) {
      return null;
    }
    // Sort by created_at descending and return the most recent
    const sorted = profile.portfolio_items
      .filter(item => item.status === 'published' && item.created_at)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return sorted[0] || null;
  };

  // Update getFeaturedImageUrl to use getLastAddedPortfolioItem
  const getFeaturedImageUrl = (): string => {
    const lastAddedItem = getLastAddedPortfolioItem();
    if (!lastAddedItem) {
      return '/placeholder-image.svg';
    }
    if (lastAddedItem.images && lastAddedItem.images.length > 0) {
      return lastAddedItem.images[0].url;
    }
    return lastAddedItem.image_url || '/placeholder-image.svg';
  };

  // Get the last added portfolio item and the date used for sorting/display
  const getLastAddedPortfolioItemAndDate = (): { item: PortfolioItem | null, date: Date | null } => {
    if (!profile.portfolio_items || profile.portfolio_items.length === 0) {
      return { item: null, date: null };
    }
    // Find the item with the latest (updated_at || created_at)
    let latestItem = null;
    let latestDate = 0;
    profile.portfolio_items.forEach(item => {
      const dateStr = item.updated_at || item.created_at;
      const dateVal = dateStr ? new Date(dateStr).getTime() : 0;
      if (dateVal > latestDate) {
        latestDate = dateVal;
        latestItem = item;
      }
    });
    return { item: latestItem, date: latestDate ? new Date(latestDate) : null };
  };

  // Use this for both display and sorting
  const { item: lastAddedItem, date: lastPortfolioItemDate } = getLastAddedPortfolioItemAndDate();

  // Check if profile should show "New" tag (within 30 days of portfolio update)
  // Use the SAME date that's displayed in the time (lastPortfolioItemDate)
  const shouldShowNewTag = (): boolean => {
    // Use the same date source as the displayed time
    if (!lastPortfolioItemDate) {
      console.log(`❌ ${profile.display_name || profile.username}: No portfolio date`);
      return false;
    }
    
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - lastPortfolioItemDate.getTime()) / (1000 * 60 * 60 * 24));
    
    console.log(`📊 ${profile.display_name || profile.username}:`);
    console.log(`   Portfolio date: ${lastPortfolioItemDate.toLocaleDateString()} ${lastPortfolioItemDate.toLocaleTimeString()}`);
    console.log(`   Today's date: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`);
    console.log(`   Days ago: ${diffInDays} days`);
    console.log(`   Show tag: ${diffInDays <= 30 && diffInDays >= 0}`);
    console.log(`---`);
    
    // Only show "New" tag if portfolio updated within the last 30 days (1 month)
    // diffInDays >= 0 prevents future dates
    // diffInDays <= 30 means within 1 month
    return diffInDays <= 30 && diffInDays >= 0;
  };

  // Format the last updated time for the last added portfolio item in X/Twitter style
  const formatLastPortfolioItemTime = (): string => {
    if (!lastPortfolioItemDate) return '';
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - lastPortfolioItemDate.getTime()) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s`;
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h`;
    } else {
      // If this year, show '24 Jul', else show '2023'
      const nowYear = now.getFullYear();
      const itemYear = lastPortfolioItemDate.getFullYear();
      if (nowYear === itemYear) {
        // e.g. 24 Jul
        return `${lastPortfolioItemDate.getDate()} ${lastPortfolioItemDate.toLocaleString('default', { month: 'short' })}`;
      } else {
        return `${itemYear}`;
      }
    }
  };

  // Format country name with proper capitalization
  const formatCountryName = (country: string | null): string => {
    if (!country) return 'Unknown';
    
    // Handle common country name variations
    const countryMap: Record<string, string> = {
      'nigeria': 'Nigeria',
      'ghana': 'Ghana',
      'kenya': 'Kenya',
      'south africa': 'South Africa',
      'uganda': 'Uganda',
      'tanzania': 'Tanzania',
      'ethiopia': 'Ethiopia',
      'morocco': 'Morocco',
      'egypt': 'Egypt',
      'algeria': 'Algeria',
      'tunisia': 'Tunisia',
      'senegal': 'Senegal',
      'mali': 'Mali',
      'burkina faso': 'Burkina Faso',
      'niger': 'Niger',
      'chad': 'Chad',
      'cameroon': 'Cameroon',
      'central african republic': 'Central African Republic',
      'gabon': 'Gabon',
      'congo': 'Congo',
      'democratic republic of the congo': 'Democratic Republic of the Congo',
      'angola': 'Angola',
      'zambia': 'Zambia',
      'zimbabwe': 'Zimbabwe',
      'botswana': 'Botswana',
      'namibia': 'Namibia',
      'mozambique': 'Mozambique',
      'madagascar': 'Madagascar',
      'mauritius': 'Mauritius',
      'seychelles': 'Seychelles',
      'comoros': 'Comoros',
      'djibouti': 'Djibouti',
      'somalia': 'Somalia',
      'sudan': 'Sudan',
      'south sudan': 'South Sudan',
      'eritrea': 'Eritrea',
      'libya': 'Libya',
      'togo': 'Togo',
      'benin': 'Benin',
      'ivory coast': 'Ivory Coast',
      'cote d\'ivoire': 'Côte d\'Ivoire',
      'liberia': 'Liberia',
      'sierra leone': 'Sierra Leone',
      'guinea': 'Guinea',
      'guinea-bissau': 'Guinea-Bissau',
      'gambia': 'Gambia',
      'cape verde': 'Cape Verde',
      'sao tome and principe': 'São Tomé and Príncipe',
      'equatorial guinea': 'Equatorial Guinea',
      'rwanda': 'Rwanda',
      'burundi': 'Burundi',
      'malawi': 'Malawi',
      'lesotho': 'Lesotho',
      'eswatini': 'Eswatini',
      'swaziland': 'Eswatini', // Legacy name
      'united kingdom': 'United Kingdom',
      'united states': 'United States',
    };

    // Replace dashes with spaces first, then normalize
    const normalizedCountry = country.replace(/-/g, ' ').toLowerCase().trim();
    return countryMap[normalizedCountry] || country.replace(/-/g, ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Helper: Get portfolio items from the last 24 hours
  const getRecentPortfolioItems = () => {
    if (!profile.portfolio_items) return [];
    const now = new Date();
    return profile.portfolio_items.filter(item => {
      if (!item.created_at) return false;
      const created = new Date(item.created_at);
      return (now.getTime() - created.getTime()) <= 24 * 60 * 60 * 1000;
    });
  };

  const recentItems = getRecentPortfolioItems();
  const moreCount = recentItems.length > 1 ? recentItems.length - 1 : 0;
  const displayName = profile.display_name || profile.username || 'Unknown Designer';
  const featuredImageUrl = getFeaturedImageUrl();
  const lastPortfolioItemTime = formatLastPortfolioItemTime();
  const formattedLocation = formatCountryName(profile.location);

  // Debug portfolio data
  console.log('PortfolioFeedCard: Portfolio data', {
    profileId: profile.id,
    portfolioItemsCount: profile.portfolio_items?.length || 0,
    lastAddedItem,
    featuredImageUrl,
    portfolioItems: profile.portfolio_items
  });

  // Slugify utility (copied from AppRoutes)
  function slugify(text: string) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }
  function getPortfolioSlug(item: PortfolioItem) {
    return (item as any).slug || slugify(item.title || item.id);
  }

  // Update card click to go to the specific portfolio item (with slug)
  const handleCardClick = () => {
    if (isMobile && onOpenPortfolioModal && lastAddedItem) {
      onOpenPortfolioModal(profile, lastAddedItem);
      return;
    }
    if (lastAddedItem && (profile.username || profile.username_slug)) {
      const username = profile.username_slug || profile.username;
      const slug = getPortfolioSlug(lastAddedItem);
      navigate(`/u/${username}/portfolio/${slug}`, { state: { background: location } });
    } else if (profile.username_slug) {
      navigate(`/u/${profile.username_slug}`);
    } else if (profile.username) {
      navigate(`/u/${profile.username}`);
    } else {
      navigate(`/profile/${profile.id}`);
    }
  };

  // Handle profile image and name click to go to profile page
  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering card click
    if (profile.username_slug) {
      navigate(`/u/${profile.username_slug}`);
    } else if (profile.username) {
      navigate(`/u/${profile.username}`);
    } else {
      navigate(`/profile/${profile.id}`);
    }
  };

  return (
    <div className="relative group">
      {/* Card content */}
      <div 
        className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow duration-200"
        onClick={handleCardClick}
      >
        {/* Profile Info at the top */}
        <div className="flex items-center gap-3 px-2 pt-5 pb-2">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleProfileClick}
          >
            {profile.avatar_url ? (
            <img
                src={profile.avatar_url}
                alt={displayName}
              className="w-12 h-12 rounded-full object-cover border border-gray-100"
                onError={(e) => {
                  console.error('Avatar image failed to load:', profile.avatar_url);
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`w-12 h-12 rounded-full border border-gray-100 bg-gray-100 flex items-center justify-center ${profile.avatar_url ? 'hidden' : ''}`}>
              <span className="text-gray-500 text-lg font-semibold">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-semibold text-sm text-gray-900 truncate max-w-[120px] md:max-w-[180px] lg:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis">{displayName}</span>
                <VerifiedBadge width={15} height={15} />
                {/* Show "New" tag next to name */}
                {shouldShowNewTag() && (
                  <Badge 
                    variant="secondary" 
                    className="text-[8px] px-1 py-0.5 bg-green-100 text-green-600 border-green-200 font-light"
                  >
                    New
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-0.5">
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> {profile.profession || 'Designer'}
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-base leading-none">{getFlagEmoji(formattedLocation)}</span>
                  {formattedLocation}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Featured Portfolio Image */}
        <div className="p-2 pb-2 relative">
          {/* +N more by [Name] tag overlayed on image, only on hover (desktop), always on mobile */}
          {moreCount > 0 && (
            <a
              className={
                isMobile
                  ? "absolute top-2 right-2 z-10 cursor-pointer"
                  : "absolute top-2 right-2 z-10 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              }
              href={
                profile.username_slug
                  ? `/u/${profile.username_slug}`
                  : profile.username
                  ? `/u/${profile.username}`
                  : `/profile/${profile.id}`
              }
              title={`View all recent projects by ${displayName}`}
              onClick={e => e.stopPropagation()}
            >
              <span className="relative overflow-hidden bg-white rounded-full shadow-sm py-1 flex gap-2 items-center whitespace-nowrap px-3 text-xs font-medium text-gray-900 border border-gray-200">
                +{moreCount} more by {displayName}
              </span>
            </a>
          )}
          <div className="aspect-square w-full">
            {featuredImageUrl && featuredImageUrl !== '/placeholder-image.svg' ? (
              <img
                src={featuredImageUrl}
                alt={lastAddedItem?.title || 'Portfolio artwork'}
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  console.error('Portfolio image failed to load:', featuredImageUrl);
                  e.currentTarget.src = '/placeholder-image.svg';
                }}
              />
            ) : (
              <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <div className="text-2xl mb-2">🎨</div>
                  <div className="text-sm">No portfolio image</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioFeedCard; 